/**
 * One-input date-range filter for ublaboo DataGrid columns.
 *
 * Markup comes from {block filtertype-date-range} in
 * app/Presenters/templates/components/grid/datagrid.latte: a visible readonly input plus
 * the grid's two real from/to fields rendered as hidden inputs.
 *
 * Design notes — the bugs this module exists to not repeat:
 *
 *  - Hidden inputs carry ISO (YYYY-MM-DD). Ublaboo's DateTimeHelper::fromString tries the
 *    filter's own PHP format first and falls back to 'Y-m-d', and the raw stored string is
 *    echoed back into the form, so a value round-trips through a redraw untouched. Values a
 *    pre-existing session may still hold in the old 'j. n. Y' format parse too (FORMATS).
 *  - The picker's popup lives in <body>. resetFilter! and resetColumnFilter! redraw the whole
 *    grid snippet, which replaces the input and orphans the popup, so the previous instance
 *    is destroyed by owner id before a new one is built. Plain filtering redraws only the
 *    body, the filter row (and the picker) survive.
 *  - apply.daterangepicker does not bubble to document, so it is bound per input in init().
 *  - Autosubmit is ublaboo's, not ours: one `change` on the hidden `to` input is what
 *    www/assets/libs/ublaboo/datagrid.js (input[data-autosubmit][data-autosubmit-change])
 *    debounces into a filter-form submit. Never submit the form from here.
 *  - daterangepicker merges element.data() into its options, so the visible input carries
 *    no data-* attributes and the READY flag is set only after construction.
 *  - Day/month names and firstDay come from moment's bundled `cs` locale, which the picker
 *    reads by default; only format and separator are overridden. With autoApply the
 *    Apply/Cancel buttons never render, so there is nothing else to translate.
 */
(function (window, $) {
    "use strict";

    if (typeof $ !== "function" || !$.fn || !$.fn.daterangepicker || !window.moment) {
        return;
    }

    var SEL = {
        wrap: ".fi-dg-daterange",
        input: ".js-dg-daterange",
        from: ".js-dg-daterange-from",
        to: ".js-dg-daterange-to",
        toggle: ".js-dg-daterange-toggle",
    };

    var ISO = "YYYY-MM-DD";
    var DISPLAY = "DD.MM.YYYY";
    var SEPARATOR = " – ";
    var READY = "fiDgDrReady";
    var OWNER_ATTR = "data-fi-dg-owner";
    // ISO first; the rest are values an older session may still hold.
    var FORMATS = [ISO, "D. M. YYYY", "DD.MM.YYYY", "D.M.YYYY"];

    var pickers = {}; // owner id -> picker instance, for cross-redraw teardown
    var bound = false;

    function parse(value) {
        if (!value) {
            return null;
        }
        var m = window.moment(value, FORMATS, true);
        return m.isValid() ? m : null;
    }

    function ownerOf($wrap) {
        // Nette's field id: stable across redraws, always [\w-].
        return ($wrap.find(SEL.from).attr("id") || "").replace(/[^\w-]/g, "");
    }

    function paint($wrap, from, to) {
        var text = "";
        if (from || to) {
            text = (from ? from.format(DISPLAY) : "") + SEPARATOR + (to ? to.format(DISPLAY) : "");
        }
        $wrap.find(SEL.input).val(text);
        $wrap.toggleClass("has-value", text !== "");
    }

    /** Writes both hidden fields and fires exactly one autosubmit. */
    function commit($wrap, from, to) {
        $wrap.find(SEL.from).val(from ? from.format(ISO) : "");
        $wrap.find(SEL.to).val(to ? to.format(ISO) : "").trigger("change");
        paint($wrap, from, to);
    }

    function destroy(id) {
        if (id && pickers[id]) {
            try {
                pickers[id].hide();
                pickers[id].remove();
            } catch (e) {
                // already gone with a previous redraw
            }
            delete pickers[id];
        }
        if (id) {
            $(".daterangepicker[" + OWNER_ATTR + '="' + id + '"]').remove();
        }
    }

    /**
     * Builds the picker for every input that does not have one yet. Safe to call as often
     * as you like — after a snippet redraw the element is brand new and therefore unflagged,
     * everything else is skipped.
     */
    function init() {
        $(SEL.input).each(function () {
            var $input = $(this);
            if ($input.data(READY)) {
                return;
            }

            var $wrap = $input.closest(SEL.wrap);
            var id = ownerOf($wrap);
            destroy(id);

            var from = parse($wrap.find(SEL.from).val());
            var to = parse($wrap.find(SEL.to).val());

            $input.daterangepicker({
                autoApply: true,
                autoUpdateInput: false,
                showDropdowns: true,
                linkedCalendars: false,
                startDate: from || to || window.moment(),
                endDate: to || from || window.moment(),
                locale: { format: DISPLAY, separator: SEPARATOR },
            });

            var picker = $input.data("daterangepicker");
            if (id && picker && picker.container) {
                picker.container.attr(OWNER_ATTR, id);
                pickers[id] = picker;
            }

            $input.on("apply.daterangepicker", function (ev, p) {
                commit($wrap, p.startDate, p.endDate);
            });

            paint($wrap, from, to);
            $input.data(READY, true);
        });
    }

    function bind() {
        if (bound) {
            return;
        }
        bound = true;

        // Delegated: registered once, survives every redraw.
        $(document).on("click", SEL.toggle, function () {
            $(this).closest(SEL.wrap).find(SEL.input).trigger("click");
        });

        // resetFilter!/resetColumnFilter! redraw the whole grid including this markup.
        if ($.nette && typeof $.nette.ext === "function") {
            $.nette.ext("fi-dg-daterange", { success: init });
        }
        $(document).ajaxComplete(init);

        $(init);
    }

    bind();

    window.fiDatagridDaterange = { init: init };
})(window, window.jQuery);
